import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    useChecklistSummary,
    useCompletionReport,
    useBottleneckReport,
    useChecklists,
} from '../hooks';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export function ChecklistDashboardPage() {
    const { data: summary, isLoading: summaryLoading } = useChecklistSummary();
    const { data: completion } = useCompletionReport();
    const { data: bottlenecks } = useBottleneckReport();

    const [overdueFilter, setOverdueFilter] = useState<string>('active');
    const { data: overdueChecklists } = useChecklists({
        status: overdueFilter || undefined,
        per_page: 5,
    });

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Checklist Dashboard</h1>
                    <p className="text-sm text-muted-foreground">
                        Organisation-wide compliance overview
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link to="/checklists">
                        <Button variant="outline">All Checklists</Button>
                    </Link>
                    <Link to="/checklists/templates/new">
                        <Button>+ New Template</Button>
                    </Link>
                </div>
            </div>

            {/* Summary KPIs */}
            {summaryLoading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : summary ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <KPICard label="Active" value={summary.active} color="blue" />
                    <KPICard label="Completed" value={summary.completed} color="emerald" />
                    <KPICard label="Overdue Steps" value={summary.overdue_steps} color="red" />
                    <KPICard label="Total" value={summary.total} color="gray" />
                </div>
            ) : null}

            {/* Completion Report */}
            {completion && completion.length > 0 && (
                <section className="space-y-3">
                    <h2 className="text-lg font-semibold">Completion Report</h2>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <MetricCard
                            label="Total Completed"
                            value={String(
                                completion.reduce((sum, r) => sum + r.total_completed, 0)
                            )}
                        />
                        <MetricCard
                            label="Avg Completion Time"
                            value={`${(
                                completion.reduce((sum, r) => sum + r.avg_completion_days, 0) /
                                completion.length
                            ).toFixed(1)} days`}
                        />
                        <MetricCard
                            label="Avg Completion Rate"
                            value={`${(
                                completion.reduce((sum, r) => sum + r.completion_rate, 0) /
                                completion.length
                            ).toFixed(0)}%`}
                        />
                    </div>
                    <div className="rounded-lg border">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-muted/40">
                                    <th className="p-2 text-left font-medium">Template</th>
                                    <th className="p-2 text-right font-medium">Assigned</th>
                                    <th className="p-2 text-right font-medium">Completed</th>
                                    <th className="p-2 text-right font-medium">Avg Days</th>
                                    <th className="p-2 text-right font-medium">Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {completion.map((row) => (
                                    <tr key={row.template_id} className="border-b last:border-0">
                                        <td className="p-2">{row.template_name}</td>
                                        <td className="p-2 text-right">{row.total_assigned}</td>
                                        <td className="p-2 text-right">{row.total_completed}</td>
                                        <td className="p-2 text-right">
                                            {row.avg_completion_days.toFixed(1)}
                                        </td>
                                        <td className="p-2 text-right">
                                            {row.completion_rate.toFixed(0)}%
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Bottleneck Report */}
            {bottlenecks && bottlenecks.length > 0 && (
                <section className="space-y-3">
                    <h2 className="text-lg font-semibold">Bottleneck Steps</h2>
                    <p className="text-sm text-muted-foreground">
                        Steps most frequently overdue or blocked
                    </p>
                    <div className="rounded-lg border">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-muted/40">
                                    <th className="p-2 text-left font-medium">Step Title</th>
                                    <th className="p-2 text-left font-medium">Template</th>
                                    <th className="p-2 text-right font-medium">Times Overdue</th>
                                    <th className="p-2 text-right font-medium">Avg Delay (days)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bottlenecks.map((b, i) => (
                                    <tr key={i} className="border-b last:border-0">
                                        <td className="p-2 font-medium">{b.step_title}</td>
                                        <td className="p-2 text-muted-foreground">
                                            {b.template_name}
                                        </td>
                                        <td className="p-2 text-right">{b.times_overdue}</td>
                                        <td className="p-2 text-right">
                                            {b.avg_delay_days.toFixed(1)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Recent Checklists (quick-glance) */}
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Recent Checklists</h2>
                    <Select value={overdueFilter} onValueChange={setOverdueFilter}>
                        <SelectTrigger className="w-32">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    {overdueChecklists?.data.map((c) => (
                        <Link
                            key={c.id}
                            to={`/checklists/${c.id}`}
                            className="flex items-center justify-between rounded-lg border p-3 transition hover:bg-muted/30"
                        >
                            <div>
                                <p className="font-medium">{c.template_name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {c.subject_name} · Started{' '}
                                    {new Date(c.start_date).toLocaleDateString('en-GB')}
                                </p>
                            </div>
                            {c.progress && (
                                <span className="text-sm font-medium">
                                    {c.progress.percent}%
                                </span>
                            )}
                        </Link>
                    ))}
                    {overdueChecklists?.data.length === 0 && (
                        <p className="text-sm text-muted-foreground">None found.</p>
                    )}
                </div>
            </section>
        </div>
    );
}

/* ── Helpers ── */

function KPICard({
    label,
    value,
    color,
}: {
    label: string;
    value: number;
    color: string;
}) {
    const bg = `bg-${color}-50`;
    const text = color === 'gray' ? '' : `text-${color}-700`;
    return (
        <div className={`rounded-lg border ${bg} p-3 ${text}`}>
            <p className="text-sm">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
        </div>
    );
}

function MetricCard({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-lg border p-4">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-xl font-bold">{value}</p>
        </div>
    );
}